<template>
  <div 
    class="sidebar flex flex-col h-screen border-r border-slate-200" 
    :class="[isExpanded ? 'sidebar-expanded' : 'sidebar-collapsed', {'mobile-open': isMobileOpen}]"
    :style="{'backgroundColor': auth.user?.account?.sidebarColor ?? '#ffffff'}"
  >
    <!-- Toggle button for desktop -->
    <button 
      type="button" 
      class="toggle-button hidden md:flex text-gray-500 hover:text-gray-800" 
      @click="toggleSidebar"
    >
      <span class="material-icons-outlined text-lg">
        {{ isExpanded ? 'chevron_left' : 'chevron_right' }}
      </span>
    </button>

    <!-- Logo Section -->
    <div class="flex items-center justify-center mb-8 flex-shrink-0">
      <Link href="/" class="flex items-center space-x-2">
        <logo :src="auth.user?.account?.logo" class="h-8 w-auto flex-shrink-0" />
        <span 
          class="menu-text text-xs font-semibold text-gray-600 bg-gray-150 rounded-full px-2 py-0.5"
          :class="{'menu-text-hidden': !isExpanded}"
        >
          1.30-0-cond
        </span>
      </Link>
    </div>

    <!-- Links List -->
    <nav class="flex-1 space-y-1">
      <!-- Escritorio -->
      <Link 
        class="menu-link"
        :class="{'active': isUrl('')}"
        href="/"
      >
        <span class="material-icons-outlined menu-icon">dashboard</span>
        <span class="menu-text ml-3" :class="{'menu-text-hidden': !isExpanded}">Escritorio</span>
      </Link>

      <!-- Propietarios -->
      <Link 
        v-if="hasAccess(['admin', 'sales', 'financial'])"
        class="menu-link"
        :class="{'active': isUrl('clients')}"
        href="/clients"
      >
        <span class="material-icons-outlined menu-icon">people_outline</span>
        <span class="menu-text ml-3" :class="{'menu-text-hidden': !isExpanded}">Propietarios</span>
      </Link>

      <!-- Cobranza -->
      <Link 
        v-if="hasAccess(['sales', 'admin', 'storer', 'financial'])"
        class="menu-link"
        :class="{'active': isUrl('orders')}"
        href="/orders"
      >
        <span class="material-icons-outlined menu-icon">payments</span>
        <span class="menu-text ml-3" :class="{'menu-text-hidden': !isExpanded}">Cobranza</span>
      </Link>

      <!-- Gastos -->
      <Link 
        v-if="hasAccess(['admin', 'financial'])"
        class="menu-link"
        :class="{'active': isUrl('spents')}"
        href="/spents"
      >
        <span class="material-icons-outlined menu-icon">receipt_long</span>
        <span class="menu-text ml-3" :class="{'menu-text-hidden': !isExpanded}">Gastos</span>
      </Link>

      <!-- Reportes -->
      <Link 
        v-if="hasAccess(['sales', 'admin'])"
        class="menu-link"
        :class="{'active': isUrl('reports')}"
        href="/reports"
      >
        <span class="material-icons-outlined menu-icon">assessment</span>
        <span class="menu-text ml-3" :class="{'menu-text-hidden': !isExpanded}">Reportes</span>
      </Link>
    </nav>
  </div>
</template>

<script>
import { Link } from '@inertiajs/inertia-vue3'
import Logo from '@/Shared/Logo'

export default {
  components: {
    Link,
    Logo,
  },
  props: {
    auth: Object,
    isMobileOpen: Boolean,
  },
  data() {
    return {
      role: this.auth.user.role,
      isExpanded: localStorage.getItem('sidebarExpanded') !== 'false',
    }
  },
  methods: {
    toggleSidebar() {
      this.isExpanded = !this.isExpanded;
      localStorage.setItem('sidebarExpanded', this.isExpanded);
    },
    isUrl(...urls) {
      let currentUrl = this.$page.url.substr(1)
      if (urls[0] === '') {
        return currentUrl === ''
      }
      return urls.filter((url) => currentUrl.startsWith(url)).length
    },
    hasAccess(allowedRoles) {
      return allowedRoles.includes(this.role)
    }
  },
}
</script>

<style scoped>
/* Transiciones y contenedores */
.sidebar {
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}
.sidebar-expanded {
  width: 224px;
  overflow: hidden;
  padding: 2rem 1rem;
}
.sidebar-collapsed {
  width: 80px;
  overflow: hidden;
  padding: 2rem 1rem;
}
/* Comportamiento del texto del menú */
.menu-text {
  transition: all 0.3s ease;
  white-space: nowrap;
}
.menu-text-hidden {
  opacity: 0;
  width: 0;
  overflow: hidden;
}
.menu-icon {
  min-width: 2rem;
  width: 2rem;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.35rem;
}
/* Enlaces del menú */
.menu-link {
  padding: 0.75rem;
  border-radius: 0.5rem;
  margin: 0.25rem 0;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  color: #64748b; /* Gray-500/Slate-500 */
}
.menu-link:hover {
  color: #1e293b !important; /* Slate-800 */
  background-color: #f1f5f9; /* Slate-100 */
}
/* Estado Activo (Naranja semi-transparente) */
.menu-link.active {
  color: #f97316 !important; /* Orange-500 */
  background-color: rgba(254, 215, 170, 0.4) !important; /* Fondo naranja suave */
  font-weight: bold;
}
/* Botón flotante para colapsar en Desktop */
.toggle-button {
  position: absolute;
  right: 0.5rem;
  top: 0.5rem;
  width: 1.75rem;
  height: 1.75rem;
  background: white;
  border: 1px border #e2e8f0;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 2px 4px rgba(0,0,0,0.08);
  z-index: 10;
  transition: all 0.3s ease;
}
.toggle-button:hover {
  background: #f8fafc;
  transform: scale(1.05);
}
/* Media Query para Responsive (Mobile drawer) */
@media (max-width: 768px) {
  .sidebar {
    position: fixed !important;
    top: 0;
    left: 0;
    height: 100vh;
    z-index: 50;
    transform: translateX(-100%);
    transition: transform 0.3s ease;
    width: 240px !important;
    box-shadow: 4px 0 24px rgba(0,0,0,0.12);
  }
  .sidebar.mobile-open {
    transform: translateX(0);
  }
  .toggle-button {
    display: none;
  }
  .menu-text {
    opacity: 1 !important;
    width: auto !important;
  }
}
</style>
